import { Alert, Stack, Typography } from '@mui/material';
import { useProgressionModel } from 'features/wildfires/hooks/useProgressionModel';
import { Trans, useTranslation } from 'react-i18next';
import { getTimePass, SHORT_MONTH_DAY_12HOUR_FORMAT } from 'shared/dates';
import { formatDate } from 'date-fns';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { ProgressionModelsList } from '../ProgressionModelsList';

type ProgressionModelDrawerContentProps = {
  geoEventId: number;
  modelId: number;
};

export const ProgressionModelDrawerContent = (
  props: ProgressionModelDrawerContentProps,
): JSX.Element | null => {
  const { geoEventId, modelId } = props;
  const { t } = useTranslation();
  const { geoEvent, isGeoEventLoading } = useGeoEvent({ id: geoEventId });
  const { progressionModel } = useProgressionModel({ modelId });

  if (isGeoEventLoading || !progressionModel) {
    return <LoadingAndErrors isLoading={isGeoEventLoading} />;
  }

  return (
    <>
      <Stack gap={2} sx={{ px: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
          {geoEvent?.name}
        </Typography>

        <Stack spacing={0.5}>
          <Typography>
            <Trans
              i18nKey="wildfireProgressionModel.updated"
              values={{
                timeAgo: getTimePass(
                  progressionModel.dateModified.toISOString(),
                ),
              }}
            >
              Updated <b>1 minute ago</b>
            </Trans>
          </Typography>

          <Typography variant="subtitle1" color="textSecondary">
            <Trans
              i18nKey="wildfireProgressionModel.createdBy"
              values={{
                name: progressionModel.sourceAttribution,
                timeAgo: getTimePass(
                  progressionModel.dateCreated.toISOString(),
                ),
                date: formatDate(
                  new Date(progressionModel.dateCreated),
                  SHORT_MONTH_DAY_12HOUR_FORMAT,
                ),
              }}
            >
              Created by <b>Pyregence</b> • 5 days ago • Aug 4, 12:54 PM
            </Trans>
          </Typography>
        </Stack>

        <Alert severity="warning" icon={false}>
          {t('wildfireProgressionModel.warning')}
        </Alert>
      </Stack>

      <ProgressionModelsList
        wildfireId={geoEventId}
        title={
          <Typography variant="h3" sx={{ fontWeight: 'fontWeightBold' }}>
            {t('wildfireProgressionModel.additionalModels')}
          </Typography>
        }
        excludeModel={progressionModel.sourceAttribution}
      />
    </>
  );
};
